


import { useState, useEffect } from "react";
import { MetricCard } from "./MetricCard";
import { Clock, ChevronDown } from "lucide-react";
import "./ReportAnalysisTab.css";
import {
    BarChart, Bar, XAxis, YAxis, AreaChart, Area,
    CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import {
    getAdminStatistics
} from "../../../Servicefile/ReportAnalysisService.jsx";
function FoodOrdersTab({ data: propData, isLoading: propLoading }) {

    const [data, setData] = useState(propData || null);
    const [loading, setLoading] = useState(!propData);
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Add resize listener
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchOrdersData = async () => {

        try {

            setLoading(true);

            const result =
                await getAdminStatistics();

            console.log(
                "Orders Tab Data:",
                result
            );

            setData(result);

        } catch (err) {

            console.error(
                "Orders Tab API Error:",
                err
            );

        } finally {

            setLoading(false);
        }
    };
    useEffect(() => {

        // If parent already passed data
        if (propData) {

            setData(propData);

            setLoading(false);

            return;
        }

        fetchOrdersData();

    }, [propData]);

    const isLoading = propLoading !== undefined ? propLoading : loading;
    const isMobile = windowWidth <= 768;

    if (isLoading) return <div className="p-10 text-center">Loading...</div>;
    if (!data) return <div className="p-10 text-center">No data</div>;

    // 🔥 BASIC DATA
    const totalOrders = data.totalOrders || 0;
    const dineIn = data.dineInOrders || 0;
    const takeaway = data.takeawayOrders || 0;
    const delivery = data.deliveryOrders || 0;

    // 🔥 CATEGORY DATA (for chart)
    const ordersStatusData = Object.entries(data.revenueByCategory || {}).map(
        ([category, revenue], i) => ({
            category: isMobile && category.length > 10 ? category.substring(0, 8) + '...' : category,
            fullName: category,
            orders: Math.round(revenue / (data.avgOrderValue || 100)),
            color: ["#f97316", "#3b82f6", "#22c55e", "#a855f7"][i % 4]
        })
    );

    // 🔥 PEAK DATA - Filter for mobile (show fewer hours)
    const peakHoursData = isMobile
        ? (data.hourlyOrderBreakdown || []).filter((_, i) => i % 2 === 0)
        : (data.hourlyOrderBreakdown || []);

    // 🔥 TOGGLE
    const toggleCategory = (cat) => {
        setExpandedCategory(expandedCategory === cat ? null : cat);
    };

    const allItemsByCategory = data.allItemsByCategory || {};

    return (
        <div className="space-y-6" style={{ padding: isMobile ? "0 8px" : "0" }}>

            {/* 🔥 TOP CARDS - Responsive Grid */}
            <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
                gap: isMobile ? "10px" : "16px"
            }}>
                <MetricCard title="Total Orders" value={totalOrders} />
                <MetricCard title="Delivery" value={delivery} />
                <MetricCard title="Dine-in" value={dineIn} />
                <MetricCard title="Takeaway" value={takeaway} />
            </div>

            {/* 🔥 CATEGORY CHART */}
            <div className="dash-card p-5" style={{ padding: isMobile ? "12px" : "20px" }}>
                <h3 className="font-semibold mb-4" style={{ fontSize: isMobile ? "14px" : "16px" }}>
                    Orders by Category
                </h3>

                <ResponsiveContainer width="100%" height={isMobile ? 300 : 300} margin-left={window.innerWidth < 500 ? "-10vw" : " "} >
                    <BarChart
                        data={ordersStatusData}
                        layout={isMobile ? "vertical" : "horizontal"}
                        margin={{ top: 20, right: 30, left: isMobile ? 0 : 20, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

                        {isMobile ? (
                            // Mobile: Vertical layout with labels on bars
                            <>
                                <XAxis
                                    type="number"
                                    tick={{ fontSize: 10, fill: "#6b7280" }}
                                    domain={[0, 'dataMax + 5']}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="category"
                                    tick={{ fontSize: 11, fill: "#6b7280" }}
                                    width={85}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    formatter={(value) => [`${value} Orders`, "Total"]}
                                    labelFormatter={(label) => {
                                        const item = ordersStatusData.find(d => d.category === label);
                                        return item?.fullName || label;
                                    }}
                                    cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                                />
                                <Bar
                                    dataKey="orders"
                                    fill="#3b82f6"
                                    radius={[0, 8, 8, 0]}
                                    label={{
                                        position: 'right',
                                        fill: '#1e293b',
                                        fontSize: 11,
                                        fontWeight: 500,
                                        formatter: (value) => `${value}`
                                    }}
                                />
                            </>
                        ) : (
                            // Desktop: Horizontal layout with labels on top of bars
                            <>
                                <XAxis
                                    dataKey="category"
                                    tick={{ fontSize: 12, fill: "#6b7280" }}
                                />
                                <YAxis
                                    tick={{ fontSize: 12, fill: "#6b7280" }}
                                />
                                <Tooltip
                                    formatter={(value) => [`${value} Orders`, "Total"]}
                                    cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                                />
                                <Bar
                                    dataKey="orders"
                                    fill="#3b82f6"
                                    radius={[8, 8, 0, 0]}
                                    minPointSize={2}
                                    label={{
                                        position: 'top',
                                        fill: '#1e293b',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        formatter: (value) => `${value}`
                                    }}
                                />
                            </>
                        )}
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* 🔥 PEAK TIME */}
            <div className="dash-card p-5" style={{ padding: isMobile ? "12px" : "20px" }}>
                <div className="flex items-center gap-2 mb-1">
                    <Clock size={isMobile ? 16 : 18} className="text-orange-500" />
                    <h3 className="font-semibold" style={{ fontSize: isMobile ? "14px" : "16px" }}>
                        Peak Time Analysis
                    </h3>
                </div>

                <p className="text-xs text-gray-500 mb-4" style={{ fontSize: isMobile ? "10px" : "12px" }}>
                    Orders distribution throughout the day
                </p>

                <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                    <AreaChart data={peakHoursData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                            dataKey="hour"
                            tick={{ fontSize: isMobile ? 9 : 12, fill: "#6b7280" }}
                            interval={isMobile ? 2 : 0}
                        />
                        <YAxis tick={{ fontSize: isMobile ? 9 : 12, fill: "#6b7280" }} />
                        <Tooltip />
                        <Area
                            type="monotone"
                            dataKey="orders"
                            stroke="#f97316"
                            fill="#f97316"
                            fillOpacity={0.2}
                            strokeWidth={2}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            {/* 🔥 CATEGORY EXPANDABLE CARDS */}
            {Object.keys(allItemsByCategory).length > 0 && (
                <div className="" style={{ margin: isMobile ? " " : "0px 20%" }}>
                    <h3 className="font-semibold mb-4" style={{ fontSize: isMobile ? "16px" : "20px" }}>
                        Items Sold by Category
                    </h3>

                    <div className="space-y-3" >

                        {Object.entries(allItemsByCategory).map(([category, items]) => {

                            const totalRevenue = items.reduce((sum, i) => sum + (i.revenue || 0), 0);
                            const totalQty = items.reduce((sum, i) => sum + (i.quantity || 0), 0);

                            const isOpen = expandedCategory === category;

                            return (
                                <div
                                    key={category}
                                    className="border rounded-xl px-4 py-3 shadow-sm hover:shadow-md  hover:-translate-y-1 transition-all duration-300"
                                    style={{ padding: isMobile ? "10px 12px" : "12px 16px" }}
                                >

                                    {/* 🔥 HEADER ROW */}
                                    <div
                                        onClick={() => setExpandedCategory(isOpen ? null : category)}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: isMobile ? "1fr 60px 30px" : "1fr 80px 30px",
                                            alignItems: "center",
                                            cursor: "pointer"
                                        }}
                                    >

                                        {/* CATEGORY */}
                                        <div className="text-sm font-semibold text-gray-800" style={{ fontSize: isMobile ? "13px" : "14px" }}>
                                            {category.trim()}
                                        </div>

                                        {/* QTY (RIGHT SIDE) */}
                                        <div className="text-right text-sm font-medium text-gray-700" style={{ fontSize: isMobile ? "12px" : "13px" }}>
                                            {totalQty}
                                        </div>

                                        {/* ICON */}
                                        <div className="flex justify-end">
                                            <ChevronDown
                                                size={isMobile ? 16 : 18}
                                                className={`transition-transform duration-300 ${isOpen
                                                    ? "rotate-180 text-black-500"
                                                    : "text-black-300"
                                                    }`}
                                            />
                                        </div>

                                    </div>

                                    {/* 🔥 DROPDOWN */}
                                    {isOpen && (
                                        <div className="mt-3 pt-2 border-t space-y-2">

                                            {items.map((item, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns: isMobile ? "1fr 50px" : "1fr 80px 20px",
                                                        alignItems: "center",
                                                        fontSize: isMobile ? "12px" : "13px",
                                                        padding: isMobile ? "6px 8px" : "8px 12px",
                                                        borderRadius: "8px"
                                                    }}
                                                    className="hover:bg-orange-50 transition"
                                                >

                                                    {/* ITEM NAME */}
                                                    <div className="text-gray-700">
                                                        {item.item}
                                                    </div>

                                                    {/* QTY RIGHT */}
                                                    <div className="text-right text-gray-600 font-medium">
                                                        {item.quantity}
                                                    </div>

                                                    {/* {!isMobile && <div></div>} */}

                                                </div>
                                            ))}

                                        </div>
                                    )}

                                </div>
                            );
                        })}

                    </div>
                </div>
            )}

        </div>
    );
} export default FoodOrdersTab;